import React, { useEffect, useRef, useState } from "react";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import TimePicker from "@amzn/meridian/time-picker";
import Select, { SelectOption } from "@amzn/meridian/select";
import { TIME_PATTERN } from "../../../util/Constants";
import { SSD_BREAK_TYPE } from "../SettingsConstant";
import Text from "@amzn/meridian/text";
import { isTimeWithinInterval, isValidTimeFormat } from "../../SettingsUtil";
import { ModalFooter } from "@amzn/meridian/modal";
import Button from "@amzn/meridian/button";
import { chainWalk } from "../../../util/Util";
import Toaster from "@amzn/meridian/toaster";
import Alert from "@amzn/meridian/alert";
import { WeekdaySelector } from "../../../common/WeekdaySelector";
import {
  DAYS_OF_WEEK,
  getLocalizedDayOfWeek,
  getNumericDayOfWeek,
} from "../../../util/DateTimeUtil";
import Popover, { PopoverHeader } from "@amzn/meridian/popover";

const BREAK_TYPE_DISPLAY_NAME = {
  [SSD_BREAK_TYPE.UNPAID]: "Unpaid Break",
  [SSD_BREAK_TYPE.PAID]: "Paid Break",
  [SSD_BREAK_TYPE.SOS_STANDUP]: "Standup",
  [SSD_BREAK_TYPE.SITE_SHUTDOWN]: "Daily Shutdown",
};

const UpsertBreak = ({
  breakConfig,
  allBreaks,
  onSubmit,
  onCancel,
  isEdit,
  breakConfigs,
}) => {
  const [toasts, setToasts] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const confirmButtonRef = useRef();

  const onCloseToast = (id) => setToasts(toasts.filter((t) => t.id !== id));
  const [updatedBreakConfig, setUpdatedBreakConfig] = useState(
    JSON.parse(JSON.stringify(breakConfig)),
  );

  const onClosePopover = () => setPopoverOpen(false);

  const matchingDays = DAYS_OF_WEEK.filter((dayOfWeek) =>
    allBreaks[dayOfWeek].find(
      (breakConfig) =>
        JSON.stringify(breakConfig) === JSON.stringify(updatedBreakConfig),
    ),
  ).map(getNumericDayOfWeek);

  useEffect(() => {
    setSelectedDays([...new Set([...selectedDays, ...matchingDays])]);
  }, [updatedBreakConfig.name]);

  const onChangeBreakType = (val) => {
    setUpdatedBreakConfig({
      ...updatedBreakConfig,
      break_type: val,
      break_group_name: getBreakLabel(
        val,
        updatedBreakConfig.break_start_time_local,
      ),
    });
  };
  const onChangeStartTime = (val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      setUpdatedBreakConfig({
        ...updatedBreakConfig,
        break_start_time_local: val,
        break_group_name: getBreakLabel(updatedBreakConfig.break_type, val),
      });
    }
  };
  const onChangeEndTime = (val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      setUpdatedBreakConfig({
        ...updatedBreakConfig,
        break_end_time_local: val,
      });
    }
  };

  const onClickSubmit = () => {
    const daysToSubmit = selectedDays.map((day) => DAYS_OF_WEEK[day]);

    const { break_start_time_local, break_end_time_local, break_type } =
      updatedBreakConfig;
    if (!break_type) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please select a category.`,
        }),
      );
    }
    if (!isValidTimeFormat(break_start_time_local, TIME_PATTERN)) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please select a start time.`,
        }),
      );
    }
    if (!isValidTimeFormat(break_end_time_local, TIME_PATTERN)) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please select an end time.`,
        }),
      );
    }
    if (break_start_time_local === break_end_time_local) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Start time cannot be identical to end time`,
        }),
      );
    }

    const overlappingBreaks = getOverlappingBreaks(
      allBreaks,
      breakConfig,
      updatedBreakConfig,
      daysToSubmit,
    );
    if (overlappingBreaks.length > 0) {
      const [breakDayOfWeek, overlapDayOfWeek, overlapBreak] =
        overlappingBreaks[0];

      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Break for ${breakDayOfWeek} overlaps with ${overlapDayOfWeek} ${overlapBreak}`,
        }),
      );
    }
    onSubmit([...daysToSubmit], updatedBreakConfig);
    onCancel();
  };

  return (
    <Column>
      <React.Fragment>
        <Toaster toasts={toasts} onCloseToast={onCloseToast}>
          {(toast) => (
            <Alert toast={true} onClose={toast.onClose} type="error">
              {toast.message}
            </Alert>
          )}
        </Toaster>
      </React.Fragment>
      <Row>
        <Column>
          <Text tag="label" type="b200" color="primary">
            Category *
          </Text>
          <Select
            value={updatedBreakConfig.break_type}
            onChange={onChangeBreakType}
            placeholder="Select a category"
            width={500}
            disabled={isEdit}
          >
            {Object.values(SSD_BREAK_TYPE).map((breakType) => (
              <SelectOption
                value={breakType}
                label={BREAK_TYPE_DISPLAY_NAME[breakType]}
              />
            ))}
          </Select>
          <Text tag="label" type="b200" color="primary">
            Days of Week *
          </Text>
          <WeekdaySelector value={selectedDays} onChange={setSelectedDays} />
        </Column>
      </Row>
      <Row width="100%" alignmentHorizontal="justify">
        <Column>
          <Text tag="label" type="b200" color="primary">
            Start time*
          </Text>
          <TimePicker
            placeholder="MM:HH"
            value={updatedBreakConfig.break_start_time_local}
            onChange={onChangeStartTime}
            width={240}
          />
        </Column>
        <Column>
          <Text tag="label" type="b200" color="primary">
            End time*
          </Text>
          <TimePicker
            placeholder="MM:HH"
            value={updatedBreakConfig.break_end_time_local}
            onChange={onChangeEndTime}
            width={240}
          />
        </Column>
      </Row>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" size="small" onClick={onCancel}>
            Close
          </Button>
          <Button
            type="primary"
            size="small"
            ref={confirmButtonRef}
            onClick={() => {
              if (selectedDays.length > 1) {
                setPopoverOpen(true);
              } else {
                onClickSubmit();
              }
            }}
          >
            Confirm
          </Button>
          <Popover
            anchorNode={confirmButtonRef.current}
            position={"top"}
            open={popoverOpen}
            onClose={onClosePopover}
          >
            <PopoverHeader closeButton={onClosePopover}>
              {isEdit ? "Modify" : "Create"} break for{" "}
              {selectedDays
                .map((day) => getLocalizedDayOfWeek(day, "short"))
                .join(", ")}
              ?
            </PopoverHeader>
            <Row alignmentHorizontal="end">
              <Button
                onClick={() => onClickSubmit()}
              >{`Confirm ${selectedDays.length} day(s)`}</Button>
            </Row>
          </Popover>
        </Row>
      </ModalFooter>
    </Column>
  );
};

const getOverlappingBreaks = (
  allBreaks,
  originalBreak,
  updatedBreak,
  daysOfWeek,
) => {
  const overlappingBreaks = [];
  daysOfWeek.forEach((dayOfWeek) => {
    const prevDayOfWeek =
      DAYS_OF_WEEK[(((getNumericDayOfWeek(dayOfWeek) - 1) % 7) + 7) % 7];
    const nextDayOfWeek =
      DAYS_OF_WEEK[(getNumericDayOfWeek(dayOfWeek) + 1) % 7];

    const prevDayBreaks = allBreaks[prevDayOfWeek];
    const nextDayBreaks = allBreaks[nextDayOfWeek];

    allBreaks[dayOfWeek]
      .filter((otherBreak) => otherBreak !== originalBreak)
      .filter(
        (otherBreak) =>
          isValidTimeFormat(otherBreak.break_start_time_local, TIME_PATTERN) &&
          isValidTimeFormat(otherBreak.break_end_time_local, TIME_PATTERN),
      )
      .filter(
        (otherBreak) =>
          isTimeWithinInterval(
            updatedBreak.break_start_time_local,
            otherBreak.break_start_time_local,
            otherBreak.break_end_time_local,
          ) ||
          isTimeWithinInterval(
            updatedBreak.break_end_time_local,
            otherBreak.break_start_time_local,
            otherBreak.break_end_time_local,
          ) ||
          isTimeWithinInterval(
            otherBreak.break_start_time_local,
            updatedBreak.break_start_time_local,
            updatedBreak.break_end_time_local,
          ) ||
          isTimeWithinInterval(
            otherBreak.break_end_time_local,
            updatedBreak.break_start_time_local,
            updatedBreak.break_end_time_local,
          ) ||
          (otherBreak.break_start_time_local ===
            updatedBreak.break_start_time_local &&
            otherBreak.break_end_time_local ===
              updatedBreak.break_end_time_local),
      )
      .map((otherBreak) => [dayOfWeek, dayOfWeek, otherBreak.break_group_name])
      .forEach((overlap) => overlappingBreaks.push(overlap));

    if (updatedBreak.break_start_time_local < "12:00") {
      prevDayBreaks
        .filter((breakConfig) => breakConfig.break_start_time_local >= "12:00")
        .filter((breakConfig) => breakConfig.break_end_time_local < "12:00")
        .filter(
          (breakConfig) =>
            breakConfig.break_end_time_local >
            updatedBreak.break_start_time_local,
        )
        .map((breakConfig) => [
          dayOfWeek,
          prevDayOfWeek,
          breakConfig.break_group_name,
        ])
        .forEach((overlap) => overlappingBreaks.push(overlap));
    } else if (updatedBreak.break_end_time_local < "12:00") {
      nextDayBreaks
        .filter((breakConfig) => breakConfig.break_start_time_local < "12:00")
        .filter(
          (breakConfig) =>
            breakConfig.break_start_time_local <
            updatedBreak.break_end_time_local,
        )
        .map((breakConfig) => [
          dayOfWeek,
          nextDayOfWeek,
          breakConfig.break_group_name,
        ])
        .forEach((overlap) => overlappingBreaks.push(overlap));
    }
  });

  return overlappingBreaks;
};

const getBreakLabel = (breakType, breakStart) => {
  const start = chainWalk(
    () => breakStart.split(":")[0] + breakStart.split(":")[1],
    "",
  );
  return `${start}x${BREAK_TYPE_DISPLAY_NAME[breakType]}`;
};

export default UpsertBreak;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/ssd/UpsertBreak.js